import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import {
  deleteDocumentType,
  duplicateDocumentType,
  rebuildContentUnderstandingAnalyzer,
  rollbackDocumentType,
  saveDocumentType,
  SchemaQueryKeys,
  type DocumentTypeResponse,
  type RebuildAnalyzerResponse,
  type SaveDocumentTypeRequest,
} from '../api';

/** Variables consumed by the `duplicate` mutation (Wave 9.B). */
export interface DuplicateDocumentTypeVariables {
  sourceSlug: string;
  newSlug: string;
}

/** Variables consumed by the `rollback` mutation. */
export interface RollbackDocumentTypeVariables {
  slug: string;
  toVersion: string;
}

/** Variables consumed by the `save` mutation. */
export interface SaveDocumentTypeVariables {
  slug: string;
  body: SaveDocumentTypeRequest;
}

/** Bundled mutations for the Schema Designer — invalidates the list/detail queries on success. */
export interface DocumentTypeMutations {
  /** PUT /{slug} — create-or-update. Works for both new and existing types. */
  save: UseMutationResult<DocumentTypeResponse, Error, SaveDocumentTypeVariables>;
  /** POST /{slug}/rollback — restore a prior version as a new forward version. */
  rollback: UseMutationResult<DocumentTypeResponse, Error, RollbackDocumentTypeVariables>;
  remove: UseMutationResult<null, Error, string>;
  duplicate: UseMutationResult<DocumentTypeResponse, Error, DuplicateDocumentTypeVariables>;
}

export function useDocumentTypeMutations(): DocumentTypeMutations {
  const queryClient = useQueryClient();

  const invalidateAll = (slug?: string): void => {
    void queryClient.invalidateQueries({ queryKey: SchemaQueryKeys.list() });
    if (slug !== undefined) {
      void queryClient.invalidateQueries({ queryKey: SchemaQueryKeys.detail(slug) });
      void queryClient.invalidateQueries({ queryKey: SchemaQueryKeys.versions(slug) });
    }
  };

  const save = useMutation({
    mutationFn: ({ slug, body }: SaveDocumentTypeVariables) => saveDocumentType(slug, body),
    onSuccess: (data) => invalidateAll(data.slug),
  });

  const rollback = useMutation({
    mutationFn: ({ slug, toVersion }: RollbackDocumentTypeVariables) =>
      rollbackDocumentType(slug, toVersion),
    onSuccess: (data) => invalidateAll(data.slug),
  });

  const remove = useMutation({
    mutationFn: (slug: string) => deleteDocumentType(slug),
    onSuccess: (_, slug) => invalidateAll(slug),
  });

  const duplicate = useMutation({
    mutationFn: ({ sourceSlug, newSlug }: DuplicateDocumentTypeVariables) =>
      duplicateDocumentType(sourceSlug, newSlug),
    onSuccess: (data) => invalidateAll(data.slug),
  });

  return { save, rollback, remove, duplicate };
}

/**
 * Standalone mutation hook for the `POST /document-types/{slug}/content-understanding/analyzer/rebuild`
 * admin endpoint. Lives outside the bundled `useDocumentTypeMutations` because the
 * action is provider-conditional (only visible when the resolved provider is Content
 * Understanding) and not part of the standard CRUD surface. No cache invalidation is
 * needed — the analyzer is an upstream artifact that doesn't live in any React Query
 * cache; the rebuild result is consumed only by a toast.
 */
export function useRebuildContentUnderstandingAnalyzer(): UseMutationResult<
  RebuildAnalyzerResponse,
  Error,
  string
> {
  return useMutation<RebuildAnalyzerResponse, Error, string>({
    mutationFn: (slug: string) => rebuildContentUnderstandingAnalyzer(slug),
  });
}
